import { ScrollText } from "lucide-react";
import { useEffect, useId, useState } from "react";

import { ApiError, listAudit, listMembers } from "@/api/client";
import type { AuditEvent, AuditPage, AuditQuery, Members } from "@/api/client";
import { useCan } from "@/auth/AuthProvider";
import { Button } from "@/ui/Button";
import { cn } from "@/ui/cn";
import { EmptyState } from "@/ui/EmptyState";
import { Notice } from "@/ui/Notice";
import { PageHeader } from "@/ui/PageHeader";
import { Skeleton } from "@/ui/primitives";
import { Table, Td, Th } from "@/ui/Table";

const PAGE = 50;

/**
 * What each action the API records means, grouped for the filter. An action the API
 * starts recording later still shows, under its own name, until it is added here.
 */
const ACTIONS: readonly { group: string; actions: Record<string, string> }[] = [
  {
    group: "Signing in",
    actions: {
      "auth.login": "Signed in",
      "auth.login_failed": "Failed to sign in",
      "auth.login_throttled": "Sign-in blocked after repeated failures",
      "auth.logout": "Signed out",
      "auth.password_change": "Changed their password",
      "auth.password_change_failed": "Gave a wrong current password",
      "auth.password_reset_requested": "Asked to reset their password",
      "auth.password_reset_throttled": "Reset blocked after repeated requests",
      "auth.password_reset": "Reset their password",
    },
  },
  {
    group: "Members",
    actions: {
      "invite.create": "Invited a member",
      "invite.resend": "Sent an invite again",
      "invite.revoke": "Revoked an invite",
      "invite.accept": "Accepted an invite",
      "member.update": "Changed a member",
      "user.created": "Account created",
      "user.rename": "Changed their name",
    },
  },
  {
    group: "Organization",
    actions: {
      "organization.created": "Organization created",
      "organization.rename": "Renamed the organization",
      "licence.set": "Licence set",
    },
  },
  {
    group: "Knowledge",
    actions: {
      "code_standard.confirm": "Confirmed the code standard",
      "code_standard.reinfer": "Inferred the code standard again",
    },
  },
];

const LABEL: Record<string, string> = Object.fromEntries(
  ACTIONS.flatMap((g) => Object.entries(g.actions)),
);

const SELECT =
  "h-9 rounded-md border border-line-strong bg-surface px-2.5 text-sm text-ink focus:border-accent focus:outline-none";

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

type Scalar = string | number | boolean | null;

function isScalar(value: unknown): value is Scalar {
  return value === null || ["string", "number", "boolean"].includes(typeof value);
}

/**
 * What changed, in a line or two: each plain field that differs as `key: before → after`,
 * a field that only has a value as `key: value`, and any larger value by name alone.
 */
function changes(event: AuditEvent): string[] {
  const before = event.before ?? {};
  const after = event.after ?? {};
  const lines: string[] = [];
  const large: string[] = [];
  for (const key of [...new Set([...Object.keys(before), ...Object.keys(after)])]) {
    const was: unknown = before[key];
    const now: unknown = after[key];
    if (!isScalar(was ?? null) || !isScalar(now ?? null)) {
      large.push(key);
    } else if (key in before && key in after) {
      if (was !== now) lines.push(`${key}: ${String(was)} → ${String(now)}`);
    } else {
      lines.push(`${key}: ${String(key in after ? now : was)}`);
    }
  }
  if (large.length > 0) lines.push(`${large.join(", ")} changed`);
  return lines;
}

/**
 * The organization's audit log (Phase 6 Task 11, ADR-0012): who signed in, who changed
 * members, the organization and the code standard, newest first. Owners and admins only.
 */
export function AuditScreen() {
  const canRead = useCan("audit:read");
  const actionId = useId();
  const actorId = useId();
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<AuditPage | null>(null);
  const [members, setMembers] = useState<Members["members"]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canRead) return;
    listMembers()
      .then((found) => setMembers(found.members))
      .catch(() => setMembers([]));
  }, [canRead]);

  useEffect(() => {
    if (!canRead) return;
    let live = true;
    const query: AuditQuery = { limit: PAGE, offset };
    if (action) query.action = action;
    if (actor) query.actorUserId = actor;
    listAudit(query)
      .then((found) => {
        if (!live) return;
        setData(found);
        setError(null);
      })
      .catch(
        (err: unknown) =>
          live && setError(err instanceof ApiError ? err.detail : "Could not reach the server."),
      );
    return () => {
      live = false;
    };
  }, [canRead, action, actor, offset]);

  if (!canRead) {
    return (
      <main className="mx-auto w-full max-w-6xl px-4 py-8 lg:px-8">
        <PageHeader title="Audit log" />
        <Notice tone="warn" className="mt-4">
          Only an owner or admin reads the audit log.
        </Notice>
      </main>
    );
  }

  const nameOf = new Map(members.map((m) => [m.user_id, m.name || m.email]));
  const about = (event: AuditEvent): string | null =>
    event.target_type === "user" && event.target_id && event.target_id !== event.actor_user_id
      ? (nameOf.get(event.target_id) ?? null)
      : event.target_type === "email"
        ? event.target_id
        : null;
  const filtered = Boolean(action || actor);
  const last = data ? Math.min(data.offset + data.limit, data.total) : 0;

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-8 lg:px-8">
      <PageHeader
        title="Audit log"
        description="Who signed in, and who changed members, the organization or its code standard."
      />

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <label htmlFor={actionId} className="text-sm text-ink-2">
            What happened
          </label>
          <select
            id={actionId}
            className={SELECT}
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">Anything</option>
            {ACTIONS.map(({ group, actions }) => (
              <optgroup key={group} label={group}>
                {Object.entries(actions).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor={actorId} className="text-sm text-ink-2">
            Who
          </label>
          <select
            id={actorId}
            className={SELECT}
            value={actor}
            onChange={(e) => {
              setActor(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">Anyone</option>
            {members.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.name || m.email}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error ? (
        <Notice tone="danger" className="mt-4">
          {error}
        </Notice>
      ) : null}

      <div className="mt-4">
        {data === null ? (
          error ? null : (
            <div className="space-y-2" role="status" aria-label="Loading the audit log">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )
        ) : data.events.length === 0 ? (
          <EmptyState
            icon={<ScrollText />}
            title={filtered ? "Nothing matches" : "Nothing recorded yet"}
          >
            {filtered
              ? "Nothing recorded matches these filters."
              : "Sign-ins and changes to members, the organization and its code standard show here."}
          </EmptyState>
        ) : (
          <>
            <Table aria-label="Audit log">
              <thead>
                <tr>
                  <Th>When</Th>
                  <Th>Who</Th>
                  <Th>What</Th>
                  <Th className="hidden md:table-cell">Details</Th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((event) => {
                  const target = about(event);
                  const lines = changes(event);
                  return (
                    <tr key={event.id}>
                      <Td className="whitespace-nowrap text-ink-2">{when(event.at)}</Td>
                      <Td className={cn(event.actor_user_id ? "text-ink" : "text-muted")}>
                        {event.actor_user_id
                          ? event.actor_is_staff
                            ? `Darkstar (${event.actor_name ?? "staff"})`
                            : (event.actor_name ?? "A former member")
                          : "System"}
                      </Td>
                      <Td className="text-ink">
                        <span className="block">{LABEL[event.action] ?? event.action}</span>
                        {target ? <span className="block text-ink-2">{target}</span> : null}
                      </Td>
                      <Td className="hidden text-[13px] text-ink-2 md:table-cell">
                        {lines.map((line) => (
                          <span key={line} className="block">
                            {line}
                          </span>
                        ))}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-muted">
                {data.offset + 1}–{last} of {data.total}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={data.offset === 0}
                  onClick={() => setOffset(Math.max(0, data.offset - PAGE))}
                >
                  Newer
                </Button>
                <Button
                  size="sm"
                  disabled={last >= data.total}
                  onClick={() => setOffset(data.offset + PAGE)}
                >
                  Older
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
